/*
 * CURSORS_V2: where a cursor is, described so it lands on the same thing on someone else's screen.
 *
 * A pixel position means nothing on another screen: the sidebar can be wider, a section can be
 * collapsed, the page can be scrolled or narrower. So a cursor is sent as the path of elements
 * under the pointer, from the app root down, with where the pointer sat inside each one (as a
 * fraction of its width and height), plus the letter it was over when it was over text.
 *
 * Elements marked data-cursor-anchor (and elements with a stable id, e.g. feed-item-123) are
 * found by name, anywhere under the previous anchor; the plain elements after the last anchor
 * are followed child by child. The receiver walks the same path as far as it exists on their
 * screen and places the cursor in the deepest element it found:
 *  - all of it → exact (over the same letter when it was text)
 *  - part of it (say a section is collapsed for them) → inside the deepest part they have, dimmed
 *  - on a record tab they aren't on → parked on that tab's button
 *  - scrolled out of their view → pinned to the edge of the scroll area, pointing the way
 */

export const ANCHOR_ATTR = 'data-cursor-anchor';
export const OVERLAY_ATTR = 'data-cursor-overlay';
const TAB_PREFIX = 'tab:';

const round = n => Math.round(n * 10000) / 10000;

// Ids made by libraries differ from screen to screen, so only plain ones are used
const stableId = id => !!id && !/^(headlessui|radix|react|mui)|:/.test(id);

const keyOf = el => {
  const anchor = el.getAttribute(ANCHOR_ATTR);
  if (anchor) return { a: anchor };
  if (stableId(el.id)) return { d: el.id };
  return null;
};

const sameTagIndex = el => {
  let i = 0;
  for (let s = el.previousElementSibling; s; s = s.previousElementSibling) {
    if (s.tagName === el.tagName) i += 1;
  }
  return i;
};

const usable = rect => rect.width > 0 && rect.height > 0;

const caretAt = (x, y) => {
  if (document.caretPositionFromPoint) {
    const p = document.caretPositionFromPoint(x, y);
    return p ? { node: p.offsetNode, offset: p.offset } : null;
  }
  if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y);
    return r ? { node: r.startContainer, offset: r.startOffset } : null;
  }
  return null;
};

// The box of one letter of a text node
const letterRect = (node, offset) => {
  const len = node.textContent.length;
  if (!len) return null;
  const start = Math.min(Math.max(offset, 0), len - 1);
  const range = document.createRange();
  range.setStart(node, start);
  range.setEnd(node, start + 1);
  const rect = range.getBoundingClientRect();
  return usable(rect) ? rect : null;
};

/** The letter the pointer is over, if it's over text directly inside `el` */
const describeText = (el, x, y) => {
  const caret = caretAt(x, y);
  if (!caret || caret.node?.nodeType !== 3 || caret.node.parentElement !== el) return null;
  // The caret sits between letters: check the one after it, then the one before
  // The caret sits between letters: take whichever of the two is nearer. Near is enough (in the
  // gap between lines, past the end of a line): the cursor then stays with that line of text
  // for everyone instead of hopping to a spot in the paragraph that's on another line for them.
  let best = null;
  for (const offset of [caret.offset, caret.offset - 1]) {
    const rect = offset >= 0 ? letterRect(caret.node, offset) : null;
    if (!rect) continue;
    const dx = Math.max(rect.left - x, 0, x - rect.right);
    const dy = Math.max(rect.top - y, 0, y - rect.bottom);
    const far = dx + dy;
    if (dx <= 24 && dy <= rect.height * 0.75 && (!best || far < best.far)) {
      best = { offset, rect, far };
    }
  }
  if (!best) return null;
  return {
    n: Array.prototype.indexOf.call(el.childNodes, caret.node),
    o: best.offset,
    x: round((x - best.rect.left) / best.rect.width),
    y: round((y - best.rect.top) / best.rect.height)
  };
};

/**
 * Describe the point (x, y) over `target`.
 * Returns { steps, text? }, { off: true } when the pointer is on something only this screen
 * has (a pop-up, menu or modal rendered outside the app), or null.
 */
export const describePoint = (target, x, y) => {
  if (!target || target.nodeType !== 1) return null;
  if (target.closest(`[${OVERLAY_ATTR}]`)) return null;

  const chain = [];
  for (let el = target; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    chain.push(el);
  }
  if (!chain.length) return null;
  chain.reverse();
  // Pop-ups are rendered straight onto the page body: the others don't have them open
  if (chain[0].id !== 'root') return { off: true };

  const steps = chain.map(el => {
    const step = keyOf(el) || { t: el.tagName.toLowerCase(), i: sameTagIndex(el) };
    const rect = el.getBoundingClientRect();
    if (usable(rect)) {
      step.x = round((x - rect.left) / rect.width);
      step.y = round((y - rect.top) / rect.height);
    }
    return step;
  });

  const text = describeText(target, x, y);
  return text ? { steps, text } : { steps };
};

const visible = el => !!el && usable(el.getBoundingClientRect());

// An anchor anywhere under `scope`, the nearest one first (an outer one before one nested in it)
const findAnchor = (scope, step) => {
  const selector = step.a
    ? `[${ANCHOR_ATTR}="${CSS.escape(step.a)}"]`
    : `#${CSS.escape(step.d)}`;
  const found = scope.querySelectorAll(selector);
  for (const el of found) if (visible(el)) return el;
  return null;
};

const findChild = (parent, step) => {
  let i = 0;
  for (const child of parent.children) {
    if (child.tagName.toLowerCase() !== step.t) continue;
    if (i === step.i) return visible(child) ? child : null;
    i += 1;
  }
  return null;
};

/** Where `rect` is clipped on screen by the scroll areas around `el` (and the window) */
const clipFor = el => {
  let top = 0;
  let left = 0;
  let bottom = window.innerHeight;
  let right = window.innerWidth;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const style = getComputedStyle(p);
    if (style.overflowY !== 'visible' || style.overflowX !== 'visible') {
      const r = p.getBoundingClientRect();
      if (style.overflowY !== 'visible') {
        top = Math.max(top, r.top);
        bottom = Math.min(bottom, r.bottom);
      }
      if (style.overflowX !== 'visible') {
        left = Math.max(left, r.left);
        right = Math.min(right, r.right);
      }
    }
  }
  return { top, left, bottom, right };
};

// Our record tab's button for a tab path (UnderlinedTabs links end in the path)
const tabButton = path => {
  const bar = document.querySelector(`[${ANCHOR_ATTR}="tabs"]`);
  if (!bar) return null;
  for (const link of bar.querySelectorAll('a[href]')) {
    const href = link.getAttribute('href') || '';
    if ((href === path || href.endsWith(`/${path}`)) && visible(link)) return link;
  }
  return null;
};

/**
 * Find a described point on this screen.
 * Returns null (hide it) or { left, top, exact, edge?, tab? }:
 *  - exact: the whole path was found here
 *  - edge: 'top' | 'bottom' | 'left' | 'right' when it's scrolled out of view (left/top are
 *    then pinned inside the visible area on that side)
 *  - tab: the name of the record tab it's parked on (they're on a tab we aren't)
 */
export const locatePoint = described => {
  const steps = described?.steps;
  if (!steps?.length) return null;

  const lastAnchor = steps.reduce((at, s, i) => (s.a || s.d ? i : at), -1);
  let el = document.body;
  let deepest = null; // { el, step }
  let missed = null;
  let exact = true;

  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    // Plain elements before an anchor are only there to get to it: the anchor is found by name
    if (i < lastAnchor && !(step.a || step.d)) continue;
    const next = step.a || step.d ? findAnchor(el, step) : findChild(el, step);
    if (!next) {
      exact = false;
      missed = step;
      break;
    }
    el = next;
    if (step.x !== undefined) deepest = { el, step };
  }

  // On a tab we aren't on: park on that tab's button
  if (missed?.a?.startsWith(TAB_PREFIX)) {
    const path = missed.a.slice(TAB_PREFIX.length);
    const button = tabButton(path);
    if (!button) return null;
    const r = button.getBoundingClientRect();
    const label = button.textContent.trim() || path;
    return place(button, r.left + r.width * 0.5, r.top + r.height * 0.75, false, label);
  }

  // Nothing of the app found beyond the outer frame (the root or the main area)
  if (!deepest || deepest.el.id === 'root') return null;

  if (exact && described.text) {
    const node = deepest.el.childNodes[described.text.n];
    const rect = node?.nodeType === 3 ? letterRect(node, described.text.o) : null;
    if (rect) {
      return place(
        deepest.el,
        rect.left + described.text.x * rect.width,
        rect.top + described.text.y * rect.height,
        true
      );
    }
  }

  const rect = deepest.el.getBoundingClientRect();
  const x = Math.min(Math.max(deepest.step.x, 0), 1);
  // Not all of it here (say a section that's closed for us): on a short stand-in like a section
  // heading, keep to its middle, so moving up and down their open section doesn't jiggle it
  const y =
    !exact && rect.height < 80 ? 0.5 : Math.min(Math.max(deepest.step.y, 0), 1);
  return place(deepest.el, rect.left + x * rect.width, rect.top + y * rect.height, exact);
};

const EDGE_INSET = 6;

const place = (el, left, top, exact, tab = null) => {
  const clip = clipFor(el);
  // A scroll area we can't see at all (collapsed to nothing): hide
  if (clip.bottom - clip.top < 8 || clip.right - clip.left < 8) return null;
  let edge = null;
  if (top < clip.top) edge = 'top';
  else if (top > clip.bottom) edge = 'bottom';
  else if (left < clip.left) edge = 'left';
  else if (left > clip.right) edge = 'right';
  if (edge) {
    left = Math.min(Math.max(left, clip.left + EDGE_INSET), clip.right - EDGE_INSET);
    top = Math.min(Math.max(top, clip.top + EDGE_INSET), clip.bottom - EDGE_INSET);
  }
  return { left, top, exact, edge, tab };
};
